import { useGetFolioNumbers } from "creso-core/hooks";
import { FolioNumber } from "creso-core/types";
import { getFolioOptions } from "creso-core/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  cn,
} from "web-design-system";
import { useMemo } from "react";

interface Props {
  amc_id?: number | null;
  value: string;
  onChange: (value: string | number) => void;
  investmentAccountGuid: string;
  filters?: ((folio: FolioNumber) => boolean)[];
  disabled?: boolean;
}

export function FolioNumberDropdown(props: Props) {
  const { investmentAccountGuid, onChange, value, disabled = false } = props;

  const { data: folioNumbers } = useGetFolioNumbers({
    investment_account_guid: investmentAccountGuid,
  });

  const options = useMemo(() => {
    return getFolioOptions(
      (folioNumbers || [])
        .filter((d) => {
          return d.amc_id === props.amc_id;
        })
        .filter((d) => {
          return props.filters?.every((f) => f(d));
        })
    );
  }, [folioNumbers, props.amc_id, props.filters]);

  const hasCresoFolios = useMemo(() => {
    return options.some((option) => option.internal_flag);
  }, [options]);

  return (
    <div className="relative">
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder="Select folio number" />
        </SelectTrigger>
        <SelectContent className="max-h-50 overflow-y-auto">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex flex-row items-center justify-between gap-1 overflow-hidden">
                <span>{option.label}</span>
                <Badge
                  variant="custom"
                  className={cn(
                    option.internal_flag
                      ? "bg-tag-slate text-tag-slate-foreground"
                      : "bg-tag-yellow text-tag-yellow-foreground",
                    "text-xs",
                    option.internal_flag === null && "hidden"
                  )}
                >
                  {option.internal_flag ? "Internal" : "External"}
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCresoFolios && <RedDot className="absolute -right-0.5 -top-0.5" />}
    </div>
  );
}

function RedDot({ className }: { className?: string }) {
  return (
    <div className={cn("h-2 w-2 rounded-full bg-red-700", className)}></div>
  );
}
